import React, { useState, useEffect } from 'react';
import './AvailabilityManagement.css';
import apiService from '../services/api.service';
import LoadingIndicator from './LoadingIndicator';

// Time conversion utilities
const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const weekDayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const AvailabilityManagement = () => {
  const [availabilities, setAvailabilities] = useState({});
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()).toISOString().split('T')[0]);
  const [employees, setEmployees] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [existingShifts, setExistingShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: '' });
  
  // Get Monday of current week
  function getMonday(d) {
    d = new Date(d);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    return new Date(d.setDate(diff));
  }
  
  // Generate dates for the current week
  const getWeekDates = () => {
    const dates = [];
    const monday = new Date(currentWeekStart);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        dayName: weekDayNames[i],
        displayDate: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'numeric' })
      });
    }
    
    return dates;
  };
  
  // Week navigation functions
  const getPreviousWeek = () => {
    const monday = new Date(currentWeekStart);
    monday.setDate(monday.getDate() - 7);
    setCurrentWeekStart(monday.toISOString().split('T')[0]);
  };
  
  const getNextWeek = () => {
    const monday = new Date(currentWeekStart);
    monday.setDate(monday.getDate() + 7);
    setCurrentWeekStart(monday.toISOString().split('T')[0]);
  };
  
  const goToCurrentWeek = () => {
    setCurrentWeekStart(getMonday(new Date()).toISOString().split('T')[0]);
  };

  // Load data when component mounts or week changes
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
  
      try {
        const [
          employeesResponse,
          shiftTypesResponse,
          availabilitiesResponse,
          shiftsResponse
        ] = await Promise.all([
          apiService.getEmployees({ isActive: true }),
          apiService.getShiftTypes(),
          apiService.getWeeklyAvailabilities(currentWeekStart),
          apiService.getWeeklyShifts(currentWeekStart),
        ]);
  
        setEmployees(employeesResponse.data || []);
        setShiftTypes(
          (shiftTypesResponse.data || []).sort(
            (a, b) => a.defaultStartTime - b.defaultStartTime
          )
        );
  
        const shifts = Array.isArray(shiftsResponse.data)
          ? shiftsResponse.data
          : [];
        setExistingShifts(shifts);
  
        // --- Group your availabilities by date & shiftType here ---
        const grouped = {};
        (availabilitiesResponse.data || []).forEach(avail => {
          const { date, shiftTypeId } = avail;
          if (!grouped[date]) grouped[date] = {};
          if (!grouped[date][shiftTypeId]) grouped[date][shiftTypeId] = [];
          grouped[date][shiftTypeId].push({
            ...avail,
            isAssigned: false  // or derive from existingShifts
          });
        });
        setAvailabilities(grouped);
  
        // optional: you could also mark `isAssigned` on any avail that
        // already has a shift in `existingShifts`, if you want.
  
      } catch (err) {
        console.error(err);
        setError('Uygunluk bilgileri yüklenirken hata oluştu');
      } finally {
        // NO MATTER WHAT: turn off loading
        setLoading(false);
      }
    };
  
    loadData();
  }, [currentWeekStart]);
  
  
  
  // Check if employee already has a shift on the given date
  const hasExistingShift = (employeeId, date) => {
    // Make sure existingShifts is an array before calling .some()
    return Array.isArray(existingShifts) && existingShifts.some(shift => 
      shift.employeeId === employeeId && 
      shift.date === date
    );
  };
  
  // Get existing shift details for an employee on a specific date
  const getExistingShiftDetails = (employeeId, date) => {
    if (!Array.isArray(existingShifts)) return null;
    
    const shift = existingShifts.find(s => 
      s.employeeId === employeeId && 
      s.date === date
    );
    
    if (!shift) return null;
    
    const shiftType = shiftTypes.find(type => type.id === shift.shiftTypeId);
    
    return {
      shiftId: shift.id,
      shiftTypeId: shift.shiftTypeId,
      shiftTypeName: shiftType ? shiftType.name : 'Bilinmeyen Vardiya'
    };
  };
  
  // Handle approving availability
  const handleApproveAvailability = async (availability) => {
    try {
      // Check if employee already has a shift on this date
      if (hasExistingShift(availability.employeeId, availability.date)) {
        const shiftDetails = getExistingShiftDetails(availability.employeeId, availability.date);
        showNotification(`Bu personel zaten ${shiftDetails.shiftTypeName} vardiyasına atanmış. Önce mevcut vardiyayı kaldırın.`, 'error');
        return;
      }
      
      // First update availability status to approved
      await apiService.updateAvailabilityStatus(availability.id, { status: 'approved' });
      
      // Then create a shift automatically
      const shiftType = shiftTypes.find(type => type.id === availability.shiftTypeId);
      
      const shiftData = {
        date: availability.date,
        employeeId: availability.employeeId,
        shiftTypeId: availability.shiftTypeId,
        startTime: shiftType.defaultStartTime,
        endTime: shiftType.defaultEndTime,
        isCustomTime: false,
        notes: 'Personel uygunluğu onayından oluşturuldu'
      };
      
      const shiftResponse = await apiService.createShift(shiftData);
      
      // Add the new shift to existingShifts
      setExistingShifts([
        ...existingShifts,
        {
          ...shiftData,
          id: shiftResponse.data.id
        }
      ]);
      
      // Update local state to reflect changes
      const updatedAvailabilities = { ...availabilities };
      const dateKey = availability.date;
      const shiftTypeId = availability.shiftTypeId;
      
      if (updatedAvailabilities[dateKey] && updatedAvailabilities[dateKey][shiftTypeId]) {
        updatedAvailabilities[dateKey][shiftTypeId] = updatedAvailabilities[dateKey][shiftTypeId].map(avail => {
          if (avail.id === availability.id) {
            return { 
              ...avail, 
              status: 'approved', 
              isAssigned: true,
              shiftId: shiftResponse.data.id 
            };
          }
          return avail;
        });
      }
      
      setAvailabilities(updatedAvailabilities);
      showNotification('Uygunluk onaylandı ve vardiya oluşturuldu', 'success');
    } catch (err) {
      console.error('Error approving availability:', err);
      showNotification('İşlem sırasında bir hata oluştu', 'error');
    }
  };
  
  // Handle rejecting availability
  const handleRejectAvailability = async (availabilityId) => {
    try {
      await apiService.updateAvailabilityStatus(availabilityId, { status: 'rejected' });
      
      // Update local state
      const updatedAvailabilities = { ...availabilities };
      
      Object.keys(updatedAvailabilities).forEach(date => {
        Object.keys(updatedAvailabilities[date] || {}).forEach(shiftTypeId => {
          if (updatedAvailabilities[date][shiftTypeId]) {
            updatedAvailabilities[date][shiftTypeId] = updatedAvailabilities[date][shiftTypeId].map(avail => {
              if (avail.id === availabilityId) {
                return { ...avail, status: 'rejected' };
              }
              return avail;
            });
          }
        });
      });
      
      setAvailabilities(updatedAvailabilities);
      showNotification('Uygunluk reddedildi', 'success');
    } catch (err) {
      console.error('Error rejecting availability:', err);
      showNotification('İşlem sırasında bir hata oluştu', 'error');
    }
  };
  
  // Handle unapproving and removing a shift
  const handleUnapproveAvailability = async (availability) => {
    try {
      // First, find the shift if it exists
      const shift = existingShifts.find(s => 
        s.employeeId === availability.employeeId && 
        s.date === availability.date && 
        s.shiftTypeId === availability.shiftTypeId
      );
      
      if (shift) {
        // Delete the shift
        await apiService.deleteShift(shift.id);
        
        // Remove shift from local state
        setExistingShifts(existingShifts.filter(s => s.id !== shift.id));
      }
      
      // Update availability status to pending
      await apiService.updateAvailabilityStatus(availability.id, { status: 'pending' });
      
      // Update local state
      const updatedAvailabilities = { ...availabilities };
      const dateKey = availability.date;
      const shiftTypeId = availability.shiftTypeId;
      
      if (updatedAvailabilities[dateKey] && updatedAvailabilities[dateKey][shiftTypeId]) {
        updatedAvailabilities[dateKey][shiftTypeId] = updatedAvailabilities[dateKey][shiftTypeId].map(avail => {
          if (avail.id === availability.id) {
            return { 
              ...avail, 
              status: 'pending', 
              isAssigned: false,
              shiftId: null 
            };
          }
          return avail;
        });
      }
      
      setAvailabilities(updatedAvailabilities);
      showNotification('Onay kaldırıldı ve vardiya iptal edildi', 'success');
    } catch (err) {
      console.error('Error unapproving availability:', err);
      showNotification('İşlem sırasında bir hata oluştu', 'error');
    }
  };
  
  // Show notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 3000);
  };
  
  // Get employee full name
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.name} ${employee.surname}` : 'Bilinmeyen Çalışan';
  };
  
  // Generate week dates
  const weekDates = getWeekDates();

  return (
    <div className="am-calendar-container">
      <header className="am-calendar-header">
        <h2>Personel Uygunluk Takvimi</h2>
        
        <div className="am-week-navigation">
          <button onClick={getPreviousWeek} className="am-nav-button">
            <span>«</span> Önceki Hafta
          </button>
          <button onClick={goToCurrentWeek} className="am-current-week-button">
            Bu Hafta
          </button>
          <button onClick={getNextWeek} className="am-nav-button">
            Sonraki Hafta <span>»</span>
          </button>
        </div>
        
        <div className="am-week-display">
          <h3>
            {new Date(currentWeekStart).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
            {' - '}
            {new Date(new Date(currentWeekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </h3>
        </div>
      </header>
      
      {notification.message && (
        <div className={`am-notification am-${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      {error && <div className="am-error-message">{error}</div>}
      
      {loading ? (
        <LoadingIndicator message="Uygunluk bilgileri yükleniyor..." />
      ) : (
        <div className="am-availability-calendar">
          <div className="am-calendar-grid">
            <div className="am-time-column">
              <div className="am-column-header">Vardiyalar</div>
              {shiftTypes.map(shiftType => (
                <div key={shiftType.id} className="am-shift-type" style={{ borderLeft: `4px solid ${shiftType.color}` }}>
                  <div className="am-shift-name">{shiftType.name}</div>
                  <div className="am-shift-time">
                    {minutesToTimeString(shiftType.defaultStartTime)} - {minutesToTimeString(shiftType.defaultEndTime)}
                  </div>
                </div>
              ))}
            </div>
            
            {weekDates.map(day => (
              <div key={day.date} className="am-day-column">
                <div className="am-column-header">
                  <div className="am-day-name">{day.dayName}</div>
                  <div className="am-day-date">{day.displayDate}</div>
                </div>
                
                {shiftTypes.map(shiftType => {
                  const dateAvailabilities = availabilities[day.date]?.[shiftType.id] || [];
                  
                  return (
                    <div key={`${day.date}-${shiftType.id}`} className="am-day-cell">
                      {dateAvailabilities.length > 0 ? (
                        <div className="am-employee-availabilities">
                          {dateAvailabilities.map(avail => {
                            const hasOtherShift = hasExistingShift(avail.employeeId, avail.date) && 
                                                !avail.isAssigned;
                            
                            return (
                              <div 
                                key={avail.id} 
                                className={`am-employee-availability am-status-${avail.status} ${avail.isAssigned ? 'am-assigned' : ''} ${hasOtherShift ? 'am-has-other-shift' : ''}`}
                              >
                                <div className="am-employee-info">
                                  <span className="am-employee-name">{getEmployeeName(avail.employeeId)}</span>
                                  {avail.isAssigned && <span className="am-assigned-label">✓ Atandı</span>}
                                  {hasOtherShift && (
                                    <span className="am-conflict-label" title="Bu personel başka bir vardiyada">
                                      ⚠️ Çakışma
                                    </span>
                                  )}
                                </div>
                                
                                <div className="am-availability-actions">
                                  {avail.status === 'pending' && !hasOtherShift && (
                                    <>
                                      <button 
                                        className="am-approve-button"
                                        onClick={() => handleApproveAvailability(avail)}
                                        title="Onayla ve Vardiya Oluştur"
                                      >
                                        ✓
                                      </button>
                                      <button 
                                        className="am-reject-button"
                                        onClick={() => handleRejectAvailability(avail.id)}
                                        title="Reddet"
                                      >
                                        ✕
                                      </button>
                                    </>
                                  )}
                                  
                                  {avail.status === 'rejected' && !hasOtherShift && (
                                    <button 
                                      className="am-approve-button"
                                      onClick={() => handleApproveAvailability(avail)}
                                      title="Onayla ve Vardiya Oluştur"
                                    >
                                      ✓
                                    </button>
                                  )}
                                  
                                  {avail.status === 'approved' && (
                                    <button 
                                      className="am-unapprove-button"
                                      onClick={() => handleUnapproveAvailability(avail)}
                                      title="Onayı Kaldır ve Vardiyayı İptal Et"
                                    >
                                      ↺
                                    </button>
                                  )}
                                  
                                  {hasOtherShift && (
                                    <div className="am-conflict-info">
                                      Başka vardiyada: {getExistingShiftDetails(avail.employeeId, avail.date)?.shiftTypeName}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="am-no-availability">
                          <span>Uygunluk yok</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityManagement;